import { Link, useParams } from "react-router-dom";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import { CONDITIONS, CONDITION_NOTE, conditionBySlug } from "@/lib/conditions";
import {
  breadcrumbStructuredData,
  conditionPath,
  conditionSeoMetadata,
  conditionStructuredData,
} from "@/lib/local-seo";
import { PATIENT_GUIDE } from "@/lib/patient-guide";
import { usePublicSpecialties } from "@/lib/public-directory";
import { SEO } from "@/lib/seo";
import { specialtyHeading } from "@/lib/structured-data";
import { SearchX } from "lucide-react";
import { ButtonLink, EmptyState, Panel } from "@/components/system";

/** /conditions/:conditionSlug — "who treats X in Canberra", routed to specialty pages. */
export default function ConditionPage() {
  const { conditionSlug = "" } = useParams<{ conditionSlug: string }>();
  const { city } = useCity();
  const specialties = usePublicSpecialties(city.slug);
  const condition = conditionBySlug(conditionSlug);

  if (!condition) {
    return (
      <PublicShell>
        <SEO
          title="Condition not found · PulseBoard"
          description="This condition page does not exist."
          path={conditionPath(conditionSlug)}
          noindex
        />
        <div className="ds-container ds-container--narrow">
          <div className="ds-section">
            <EmptyState
              icon={<SearchX />}
              title="Condition not found"
              action={
                <div className="ds-cluster">
                  <ButtonLink to="/conditions">Browse conditions</ButtonLink>
                  <ButtonLink to="/directory" variant="secondary">Browse by specialty</ButtonLink>
                </div>
              }
            >
              This guide may have moved.
            </EmptyState>
          </div>
        </div>
      </PublicShell>
    );
  }

  const routed = condition.specialties.map((slug) => {
    const live = specialties.data?.find((item) => item.slug === slug);
    return { slug, name: live?.name ?? slug, count: live?.count ?? 0 };
  });
  const listingCount = routed.reduce((sum, item) => sum + item.count, 0);
  const metadata = conditionSeoMetadata(condition, listingCount);

  return (
    <PublicShell className="pb-condition-page">
      {specialties.isSuccess ? (
        <SEO
          title={metadata.title}
          description={metadata.description}
          path={metadata.path}
          noindex={metadata.noindex}
          follow
          jsonLd={[
            conditionStructuredData(condition, metadata, routed.map((item) => item.name)),
            breadcrumbStructuredData([
              { name: "Home", path: "/" },
              { name: "Conditions", path: "/conditions" },
              { name: condition.heading, path: metadata.path },
            ]),
          ]}
        />
      ) : null}

      <div className="ds-container">
        <header className="ds-page-head">
          <ol className="ds-crumbs" aria-label="Breadcrumb">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/conditions">Conditions</Link></li>
          </ol>
          <p className="ds-eyebrow ds-eyebrow--teal">Patient guide · {city.name}</p>
          <h1 className="ds-display-1">{condition.heading}</h1>
        </header>

        <div className="ds-split">
          <Panel aria-labelledby="condition-who">
            <h2 id="condition-who" className="ds-title">Who treats {condition.name}?</h2>
            <p className="ds-body">{condition.summary}</p>
            <p className="ds-fine">{CONDITION_NOTE}</p>
          </Panel>
          <Panel tone="tint" aria-labelledby="condition-specialists">
            <h2 id="condition-specialists" className="ds-title">Find a specialist in {city.name}</h2>
            <ul className="ds-link-grid">
              {routed.map((item) => (
                <li key={item.slug}>
                  <Link to={`/directory/${item.slug}`}>
                    <span>{specialtyHeading(item.slug, item.name, city.name)}</span>
                    {specialties.isSuccess ? <span className="ds-num">{item.count}</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="ds-small"><Link className="ds-link" to={PATIENT_GUIDE.path}>How referrals, Medicare and costs work</Link></p>
          </Panel>
        </div>

        <section className="ds-section">
          <p className="ds-small"><Link className="ds-link" to="/conditions">All conditions</Link></p>
        </section>
      </div>
    </PublicShell>
  );
}

/** /conditions — index of every condition page. */
export function ConditionsIndex() {
  const { city } = useCity();
  return (
    <PublicShell className="pb-condition-page">
      <SEO
        title="Which specialist do I need? Canberra conditions A–Z"
        description={`Find which kind of specialist treats a condition in ${city.name}, from knee pain to ADHD assessment, and how GP referrals work.`}
        path="/conditions"
        jsonLd={breadcrumbStructuredData([
          { name: "Home", path: "/" },
          { name: "Conditions", path: "/conditions" },
        ])}
      />
      <div className="ds-container">
        <header className="ds-page-head">
          <p className="ds-eyebrow ds-eyebrow--teal">Patient guide · {city.name}</p>
          <h1 className="ds-display-1">Which specialist do I need?</h1>
          <p className="ds-lead">Find the kind of specialist who usually treats a condition in {city.name}. {CONDITION_NOTE}</p>
        </header>
        <section className="ds-section ds-section--flush" aria-label="Conditions">
          <ul className="ds-link-grid">
            {[...CONDITIONS]
              .sort((a, b) => a.heading.localeCompare(b.heading))
              .map((condition) => (
                <li key={condition.slug}>
                  <Link to={conditionPath(condition.slug)}><span>{condition.heading.replace(/ in Canberra$/, "")}</span></Link>
                </li>
              ))}
          </ul>
        </section>
      </div>
    </PublicShell>
  );
}
